import React, { useState, useMemo, useEffect } from "react";
import useSWR from "swr";
import { styled } from "@mui/material/styles";
import { toast } from "react-toastify";
import moment from "moment/moment";
import MapView from "./MapView";
import ToggleOnIcon from "@mui/icons-material/ToggleOn";
import ToggleOffIcon from "@mui/icons-material/ToggleOff";
import CustomButton from "../../components/CustomButton";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Container from "@mui/material/Container";
import Typography from "@mui/material/Typography";
import Tooltip, { tooltipClasses } from "@mui/material/Tooltip";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Checkbox from "@mui/material/Checkbox";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Loader from "../../components/Loader";
import IconButton from "@mui/material/IconButton";
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
  createColumnHelper,
  getSortedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFacetedMinMaxValues,
} from "@tanstack/react-table";

import { rankItem } from "@tanstack/match-sorter-utils";

import KeyboardDoubleArrowLeftIcon from "@mui/icons-material/KeyboardDoubleArrowLeft";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardDoubleArrowRightIcon from "@mui/icons-material/KeyboardDoubleArrowRight";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import ChecklistIcon from "@mui/icons-material/Checklist";
import FilterAltIcon from "@mui/icons-material/FilterAlt";

import SuperAdminLayout from "../../layouts/SuperAdminLayout";
import DebouncedInput from "../../components/DebouncedInput";

import { getVidoesAPI, getVidoesbyId } from "../../apis/videos.apis";
import { Stack, TextField } from "@mui/material";
import ASearchFilter from "./ASearchFilter";
import base_url from "../../constants/base_url";

const columnHelper = createColumnHelper();

const speedCell = ({ getValue }) => `${getValue()}km/hr`;

const columns = [
  {
    accessorKey: "site_image",
    header: "Site Image",
    cell: ({ row }) => (
      <img
        src={base_url + "files/images/" + row.original.site_image}
        alt="thumbnail"
        style={{
          width: 60,
          height: 60,
          objectFit: "cover",
          borderRadius: 4,
        }}
      />
    ),
    enableSorting: false,
    enableColumnFilter: false,
  },
  columnHelper.accessor("filename", {
    cell: (info) => {
      const filename = info.getValue();

      const fileurl = base_url + "videos/uploads/" + filename;

      return (
        <a href={fileurl} target="_blank" rel="noreferrer">
          {filename}
        </a>
      );
    },
    header: "Video File name",
    enableColumnFilter: false,
  }),
  columnHelper.accessor("id", {
    header: "Assest Id",
    enableColumnFilter: false,
    cell: (info) => {
      const assetId = info.row.original.id;

      const aurl = "/add-asset/" + assetId;

      return (
        <a href={aurl} target="_blank" rel="noreferrer">
          {assetId}
        </a>
      );
    },
  }),

  columnHelper.accessor("video_id", {
    header: "Video Id",
    enableColumnFilter: false,
  }),

  columnHelper.accessor("tracker_id", {
    header: "Tracker Id",
    enableColumnFilter: false,
  }),

  columnHelper.accessor("average_areas", {
    header: "Frame Size",
  }),

  columnHelper.accessor("visibility_duration", {
    header: "Visibility Duration",
  }),

  columnHelper.accessor("central_duration", {
    header: "Focal Vision Duration",
  }),

  columnHelper.accessor("near_p_duration", {
    header: "Near Peripheral Duration",
  }),

  columnHelper.accessor("mid_p_duration", {
    header: "Mid Peripheral Duration",
  }),

  columnHelper.accessor("far_p_duration", {
    header: "Far Peripheral Duration",
  }),

  columnHelper.accessor("distance_to_center", {
    header: "Distance From Center",
  }),
  columnHelper.accessor("near_p_distance", {
    header: "Near Peripheral Distance",
  }),

  columnHelper.accessor("mid_p_distance", {
    header: "Mid Peripheral Distance",
  }),

  columnHelper.accessor("far_p_distance", {
    header: "Far Peripheral Distance",
  }),

  columnHelper.accessor("average_speed", {
    header: "Average Speed",
  }),

  columnHelper.accessor("length_of_stretch", {
    header: "Length Of Stretch",
  }),

  columnHelper.accessor("vendor_name", {
    header: "Vendor Name",
  }),

  columnHelper.accessor("location", {
    header: "Location",
  }),

  columnHelper.accessor("media_type", {
    header: "Media Type",
  }),

  columnHelper.accessor("illumination", {
    header: "Illumination",
  }),

  columnHelper.accessor("area", {
    header: "area",
  }),

  columnHelper.accessor("cost_for_duration", {
    header: "Cost For Duration",
  }),

  columnHelper.accessor("total_cost", {
    header: "Total Cost",
  }),

  columnHelper.accessor("zone_name", {
    header: "Zone Name",
  }),
  columnHelper.accessor("state_name", {
    header: "State Name",
  }),
  columnHelper.accessor("city_name", {
    header: "City Name",
  }),
  columnHelper.accessor("printing_rate", {
    header: "Printing Rate",
  }),

  columnHelper.accessor("mounting_rate", {
    header: "Mounting Rate",
  }),

  columnHelper.accessor("created_at", {
    header: "Created At",
    enableColumnFilter: false,
    cell: (info) => {
      const date_created = info.getValue();

      const parsedTimestamp = moment.utc(
        date_created,
        "ddd, DD MMM YYYY HH:mm:ss [GMT]"
      );

      const localTimestamp = parsedTimestamp
        .local()
        .format("ddd, DD MMM YYYY HH:mm:ss");

      return localTimestamp;
    },
    sortingFn: (rowA, rowB, columnId) => {
      const dateA = moment(
        rowA.original[columnId],
        "ddd, DD MMM YYYY HH:mm:ss [GMT]"
      );
      const dateB = moment(
        rowB.original[columnId],
        "ddd, DD MMM YYYY HH:mm:ss [GMT]"
      );

      if (dateA.isBefore(dateB)) {
        return -1;
      } else if (dateA.isAfter(dateB)) {
        return 1;
      } else {
        return 0;
      }
    },
  }),
];

const fuzzyFilter = (row, columnId, value, addMeta) => {
  const itemRank = rankItem(row.getValue(columnId), value);

  addMeta({
    itemRank,
  });

  return itemRank.passed;
};

const StyledTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: "#fff",
    color: "rgba(0, 0, 0, 0.87)",
    maxWidth: 800,
    fontSize: theme.typography.pxToRem(12),
    border: "1px solid #dadde9",
  },
}));

const GeospatialData = () => {
  const [isColumnDisplayPopUp, setIsColumnDisplayPopUp] = useState(false);
  const [filterOpen, setfilterOpen] = useState(false);
  const [sorting, setSorting] = useState();
  const [globalFilter, setGlobalFilter] = useState("");
  const [columnFilters, setColumnFilters] = useState([]);
  const [showMap, setShowMap] = useState(true);

  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });

  const { isLoading, data, mutate } = useSWR("/geospatial-data/", getVidoesAPI);
  const [columnVisibility, setColumnVisibility] = useState({
    average_areas: true,
    id: true,
    tracker_id: true,
    distance_to_center: true,
    far_p_distance: true,
    far_p_duration: true,
    mid_p_distance: true,
    mid_p_duration: true,
    near_p_distance: true,
    near_p_duration: true,
    visibility_duration: true,
    latitude: false,
    longitude: false,
    created_at: true,
    central_duration: false,
    average_speed: true,
    length_of_stretch: true,
    vendor_name: true,
    location: true,
    media_type: true,
    illumination: true,
    zone_name: true,
    city_name: true,
    state_name: true,
    area: true,
    display_cost_per_month: true,
    total_cost: true,
    video_id: false,
  });

  const table = useReactTable({
    getRowId: (row) => row.id,
    data,
    columns,
    filterFns: {
      fuzzy: fuzzyFilter,
    },
    state: {
      sorting,
      columnVisibility,
      globalFilter,
      columnFilters,
      pagination,
    },
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    getPaginationRowModel: getPaginationRowModel(),
    onPaginationChange: setPagination,
    onColumnVisibilityChange: setColumnVisibility,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    globalFilterFn: fuzzyFilter,
    getFacetedMinMaxValues: getFacetedMinMaxValues(),
    debugTable: true,
    debugHeaders: true,
    debugColumns: true,
  });

  const toggleColumnDisplayPopUp = () => {
    setIsColumnDisplayPopUp((prev) => !prev);
  };

  const handleTooltipClose = () => {
    setIsColumnDisplayPopUp(false);
  };

  const handleFilterClose = () => {
    setfilterOpen(false);
  };

  const openFilter = () => {
    setfilterOpen(true);
  };

  useEffect(() => {
    table.setGlobalFilter(""); // or some default filter to kick things off
  }, []);

  const filteredBillboards = useMemo(() => {
    if (!data) return [];

    return table
      .getFilteredRowModel()
      .rows.map((row) => row.original)
      .filter((b) => b.latitude !== null && b.longitude !== null);
  }, [data, table.getState()]);

  // Get video_ids from FILTERED billboards only
  const filteredVideoIds = useMemo(() => {
    return [...new Set(filteredBillboards.map((b) => b.video_id))];
  }, [filteredBillboards]);

  // Fetch ONLY videos associated with filtered billboards
  const { data: videosData = [], isLoading: vidsLoading } = useSWR(
    filteredVideoIds.length > 0
      ? ["videos/videos-by-ids", filteredVideoIds]
      : null,
    ([url, ids]) => getVidoesbyId(ids)
  );

  // const videoPaths = useMemo(() => {
  //   return videosData
  //     .filter((video) => video.video_id && video.snapped_coordinates?.length > 0)
  //     .map((video) => ({
  //       video_id: video.video_id,
  //       filename: video.filename,
  //       coords: video.snapped_coordinates.map((c) => [
  //         parseFloat(c.snapped_latitude),
  //         parseFloat(c.snapped_longitude),
  //       ]),
  //       speeds: video.snapped_coordinates.map((c) => c.speed || 0),
  //     }));
  // }, [videosData]);

  const videoPaths = useMemo(() => {
    return videosData
      .filter((video) => video.video_id && video.coordinates?.length > 0)
      .map((video) => {
        const coords = video.coordinates.map((c, i) => {
          const snapped = video.snapped_coordinates?.[i];
          const useSnapped =
            (snapped && snapped.snapped_latitude != 0) ||
            snapped.snapped_longitude != 0;

          return useSnapped
            ? [
                parseFloat(snapped.snapped_latitude),
                parseFloat(snapped.snapped_longitude),
              ]
            : [parseFloat(c.latitude), parseFloat(c.longitude)];
        });

        const speeds = video.coordinates.map((c) => c.speed || 0);

        return {
          video_id: video.video_id,
          filename: video.filename,
          coords,
          speeds,
        };
      });
  }, [videosData]);
  const allDataLoaded = !isLoading && !vidsLoading && data && videosData;
  if (!data) {
    return (
      <SuperAdminLayout activeLink="/videos">
        <center>
          <h1>No Data Found</h1>
        </center>
      </SuperAdminLayout>
    );
  }

  const handleEditVideoMarkets = () => {
    window.open('/geospatial-data/edit-video-markets', '_blank');
  };

  const handleAddVideoLatLong = () => {
    window.open('/geospatial-data/add-video-lat-long', '_blank');
  };
  return (
    <SuperAdminLayout activeLink="/geospatial-data" containerComponent="box">
      <Container component="div" maxWidth="xl">
        <Box display="flex" alignItems="center" gap={2}>
          <IconButton
            onClick={() => setShowMap(!showMap)}
            color={showMap ? "primary" : "default"}
            aria-label="toggle-map"
            size="large"
          >
            {showMap ? (
              <ToggleOnIcon fontSize="large" />
            ) : (
              <ToggleOffIcon fontSize="large" />
            )}
          </IconButton>

          <Typography variant="body1">
            {showMap ? "Map Visible" : "Map Hidden"}
          </Typography>

          <Box display="flex" gap={1}>
            <CustomButton variant="contained" size="small" disableElevation onClick={handleEditVideoMarkets}>
              Edit Video Markets
            </CustomButton>

            <CustomButton variant="contained" size="small" disableElevation onClick={handleAddVideoLatLong}>
              Add Video Lat-Long
            </CustomButton>
          </Box>
        </Box>

        <Box display="flex" gap={2}>
          <Box
            width={showMap ? "50%" : "0"}
            height="80vh"
            sx={{
              transition: "width 0.3s ease-in-out",
              overflow: "hidden",
              flexShrink: 0,
            }}
          >
            <MapView videos={videoPaths} billboards={filteredBillboards} />
          </Box>

          {/* Table Container */}
          <Box
            width={showMap ? "50%" : "100%"}
            sx={{ transition: "width 0.3s ease-in-out" }}
          >
            <TableContainer component={Paper}>
              <Stack
                margin={"15px"}
                justifyContent={"space-between"}
                direction={"row"}
              >
                <Stack direction="row" width="80%" alignItems="center" gap={2}>
                  <ClickAwayListener onClickAway={handleFilterClose}>
                    <div>
                      <StyledTooltip
                        PopperProps={{ disablePortal: true }}
                        placement="left-start"
                        onClose={handleFilterClose}
                        open={filterOpen}
                        disableFocusListener
                        disableHoverListener
                        disableTouchListener
                        title={
                          <Stack m={2} direction="row" flexWrap="wrap" gap={2}>
                            {table
                              .getAllColumns()
                              .filter(
                                (c) => c.getCanFilter() && c.getIsVisible()
                              )
                              .map((c) => (
                                <Box key={c.id} flex={1} minWidth="300px">
                                  <ASearchFilter column={c} table={table} />
                                </Box>
                              ))}
                          </Stack>
                        }
                      >
                        <Button
                          variant="contained"
                          size="small"
                          startIcon={<FilterAltIcon />}
                          disableElevation
                          onClick={openFilter}
                          sx={{ borderRadius: "15px" }}
                        >
                          Filters
                        </Button>
                      </StyledTooltip>
                    </div>
                  </ClickAwayListener>

                  <Box flex={1}>
                    <DebouncedInput
                      label="Global Search"
                      value={globalFilter ?? ""}
                      onChange={(value) => setGlobalFilter(String(value))}
                    />
                  </Box>
                </Stack>

                {/* Right: Column Display */}
                <ClickAwayListener onClickAway={handleTooltipClose}>
                  <div>
                    <StyledTooltip
                      PopperProps={{ disablePortal: true }}
                      placement="left-start"
                      onClose={handleTooltipClose}
                      open={isColumnDisplayPopUp}
                      disableFocusListener
                      disableHoverListener
                      disableTouchListener
                      title={
                        <List
                          sx={{
                            width: "100%",
                            display: "flex",
                            flexWrap: "wrap",
                          }}
                        >
                          <ListItemButton
                            onClick={table.getToggleAllColumnsVisibilityHandler()}
                            sx={{ flex: "1 1 25%" }}
                          >
                            <ListItemIcon>
                              <Checkbox
                                edge="start"
                                type="checkbox"
                                checked={table.getIsAllColumnsVisible()}
                                onChange={table.getToggleAllColumnsVisibilityHandler()}
                                tabIndex={-1}
                                disableRipple
                              />
                            </ListItemIcon>
                            <ListItemText
                              primary="Toggle All"
                              sx={{ fontSize: "15px" }}
                            />
                          </ListItemButton>
                          {table
                            .getAllLeafColumns()
                            .filter((c) => c.columnDef.id !== "select_col")
                            .map((column) => (
                              <ListItemButton
                                key={column.id}
                                sx={{ flex: "1 1 25%" }}
                                onClick={column.getToggleVisibilityHandler()}
                                dense
                              >
                                <ListItemIcon>
                                  <Checkbox
                                    edge="start"
                                    type="checkbox"
                                    checked={column.getIsVisible()}
                                    onChange={column.getToggleVisibilityHandler()}
                                    tabIndex={-1}
                                    disableRipple
                                  />
                                </ListItemIcon>
                                <ListItemText
                                  primary={column.columnDef.header}
                                />
                              </ListItemButton>
                            ))}
                        </List>
                      }
                    >
                      <Button
                        variant="contained"
                        size="small"
                        disableElevation
                        onClick={toggleColumnDisplayPopUp}
                        startIcon={<ChecklistIcon />}
                        sx={{ borderRadius: "15px" }}
                      >
                        Column Display
                      </Button>
                    </StyledTooltip>
                  </div>
                </ClickAwayListener>
              </Stack>

              {/* Table */}
              <Table
                sx={{ minWidth: 650 }}
                size="small"
                aria-label="a dense table"
              >
                <TableHead>
                  {table.getHeaderGroups().map((headerGroup) => (
                    <TableRow key={headerGroup.id}>
                      {headerGroup.headers.map((header) => (
                        <TableCell
                          key={header.id}
                          colSpan={header.colSpan}
                          sx={{ fontWeight: 600, cursor: "pointer" }}
                          onClick={header.column.getToggleSortingHandler()}
                        >
                          {header.isPlaceholder
                            ? null
                            : flexRender(
                                header.column.columnDef.header,
                                header.getContext()
                              )}
                          {{
                            asc: " 🔼",
                            desc: " 🔽",
                          }[header.column.getIsSorted()] ?? null}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableHead>
                <TableBody>
                  {table.getRowModel().rows.map((row) => (
                    <TableRow key={row.id}>
                      {row.getVisibleCells().map((cell) => (
                        <TableCell key={cell.id}>
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {/* Pagination */}
              <Stack
                gap={2}
                alignItems={"center"}
                margin={"15px"}
                direction={"row"}
                justifyContent={"flex-end"}
              >
                <Stack alignItems={"center"} direction={"row"} gap={1}>
                  <div>Page</div>
                  <strong>
                    {table.getState().pagination.pageIndex + 1} of{" "}
                    {table.getPageCount().toLocaleString()}
                  </strong>
                </Stack>
                <Stack alignItems={"center"} direction={"row"} gap={1}>
                  <Typography>| Go to page:</Typography>
                  <TextField
                    size="small"
                    type="number"
                    defaultValue={table.getState().pagination.pageIndex + 1}
                    onChange={(e) => {
                      const page = e.target.value
                        ? Number(e.target.value) - 1
                        : 0;
                      table.setPageIndex(page);
                    }}
                  />
                </Stack>

                <Select
                  value={table.getState().pagination.pageSize}
                  size="small"
                  onChange={(e) => table.setPageSize(Number(e.target.value))}
                >
                  {[10, 20, 30, 40, 50].map((pageSize) => (
                    <MenuItem key={pageSize} value={pageSize}>
                      Show {pageSize}
                    </MenuItem>
                  ))}
                </Select>

                <IconButton
                  size="small"
                  onClick={() => table.firstPage()}
                  disabled={!table.getCanPreviousPage()}
                >
                  <KeyboardDoubleArrowLeftIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => table.previousPage()}
                  disabled={!table.getCanPreviousPage()}
                >
                  <KeyboardArrowLeftIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => table.nextPage()}
                  disabled={!table.getCanNextPage()}
                >
                  <KeyboardArrowRightIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => table.lastPage()}
                  disabled={!table.getCanNextPage()}
                >
                  <KeyboardDoubleArrowRightIcon fontSize="small" />
                </IconButton>
              </Stack>
            </TableContainer>
          </Box>
        </Box>
      </Container>
    </SuperAdminLayout>
  );
};

export default GeospatialData;
